import React, { useState, useEffect, useRef } from 'react';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { SelectChangeEvent } from '@mui/material/Select';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import Newpagination from '../../Components/Pagination/pagination.tsx';
import { Button, Box, Typography, TextField, IconButton } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import NavBar from '../../Components/Navbar/sidenav.tsx';
import Header from '../../Components/Header/header.tsx';
import Heading from '../../Components/Heading/heading.tsx';
import { getCompanies, getInstitutions, getTickets, editTickets } from './action.ts'; 
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddTicketDialog from './add-tickets-modal.tsx';
import ConfirmationPopup from '../../Components/ConfirmationModal/confirmation-modal.tsx';

const ResponseCell = ({ 
    params, 
    onDirectSubmit, 
    onBlurSubmit 
}: { 
    params: GridRenderCellParams, 
    onDirectSubmit: (id: string, currentStatus: string, updatedRemarks: string) => void,
    onBlurSubmit: (id: string, currentStatus: string, updatedRemarks: string) => void 
}) => {
    const [remarks, setRemarks] = useState(params.value as string || '');
    const isSubmittingRef = useRef(false);
    
    const isEditing = remarks !== (params.value as string || '');

    useEffect(() => {
        setRemarks(params.value as string || '');
    }, [params.value]);

    const handleBlur = () => {
        setTimeout(() => {
            if (isEditing && !isSubmittingRef.current) {
                onBlurSubmit(String(params.row.id), params.row.status, remarks);
            }
            isSubmittingRef.current = false; 
        }, 150);
    };

    const handleDirectSubmit = () => {
        isSubmittingRef.current = true;
        onDirectSubmit(String(params.row.id), params.row.status, remarks);
    };

    return (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%" gap="5px">
            <TextField
                variant="outlined"
                size="small"
                value={remarks}
                placeholder="Type response..."
                onChange={(e) => setRemarks(e.target.value)}
                onBlur={handleBlur}
                sx={{ 
                    width: '100%',
                    '& .MuiInputBase-root': { height: '32px', fontSize: '14px' } 
                }}
            />
            {isEditing && (
                <IconButton 
                    size="small" 
                    color="success" 
                    onMouseDown={handleDirectSubmit} 
                    title="Confirm Response"
                >
                    <CheckIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
};

const TicketsPage = () => {
    const [pageSize, setPageSize] = useState<number>(10);
    const [page, setPage] = useState<number>(0);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
    const [rows, setRows] = useState<any[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<string>('');
    const [selectedInstitution, setSelectedInstitution] = useState<string>('');
    const [companyOptions, setCompanyOptions] = useState<{ id: string, label: string }[]>([]);
    const [institutionOptions, setInstitutionOptions] = useState<{ id: string, label: string }[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
    const [editingTicket, setEditingTicket] = useState<{ 
        id: string, 
        status: string, 
        remarks: string, 
        ticketNumber: string, 
        editType: 'status' | 'response' 
    } | null>(null);

    useEffect(() => {
        getCompanies(
            100, 
            0,   
            (response) => {
                const companiesList = response.message; 
                if (Array.isArray(companiesList)) {
                    const mappedCompanies = companiesList.map((item: any) => {
                        const comp = item.company;
                        return {
                            id: String(comp.id),
                            label: comp.name || `Company ${comp.id}`
                        };
                    });
                    setCompanyOptions(mappedCompanies);
                }
            },
            (errorMessage) => toast.error(typeof errorMessage === 'string' ? errorMessage : "Failed to fetch companies")
        );
    }, []);

    useEffect(() => {
        const selectedCompanyObj = companyOptions.find(c => c.id === selectedCompany);
        if (selectedCompanyObj && selectedCompanyObj.label.toLowerCase() === 'stms') {
            getInstitutions(
                100, 
                0,   
                (response) => {
                    const institutionsList = response.data;
                    if (Array.isArray(institutionsList)) {
                        const mappedInstitutions = institutionsList.map((inst: any) => ({
                            id: String(inst.id),
                            label: inst.institution_name || `Institution ${inst.id}`
                        }));
                        setInstitutionOptions(mappedInstitutions);
                    }
                },
                (errorMessage) => toast.error(typeof errorMessage === 'string' ? errorMessage : "Failed to fetch institutions")
            );
        } else {
            setInstitutionOptions([]);
            setSelectedInstitution('');
        }
    }, [selectedCompany, companyOptions]);

    useEffect(() => {
        getTickets(
            pageSize,
            page, 
            selectedCompany, 
            selectedInstitution,
            (response) => {
                const ticketsList = response.tickets || [];
                const total = response.total_records || 0;

                if (Array.isArray(ticketsList)) {
                    const mappedTickets = ticketsList.map((ticket: any, index: number) => {
                        let rawStatus = ticket.status || 'open';
                        
                        if (rawStatus === 'in_progress') rawStatus = 'In Progress';
                        
                        const formattedStatus = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);
                        const formattedDate = ticket.inserted_date 
                            ? new Date(ticket.inserted_date).toLocaleDateString('en-GB') 
                            : 'N/A';

                        return {
                            id: ticket.id,
                            slNo: page * pageSize + index + 1,
                            ticketNumber: ticket.ticket_number || '',
                            issue: ticket.title || '',
                            createdDate: formattedDate,
                            remarks: ticket.description || '',
                            status: formattedStatus,
                            assignedTo: ticket.assigned_users_id || '',
                            response: ticket.platform_remarks || ''
                        };
                    });
                    setRows(mappedTickets);
                    setTotalRecords(total);
                }
            },
            (errorMessage) => toast.error(typeof errorMessage === 'string' ? errorMessage : "Failed to fetch tickets")
        );
    }, [page, pageSize, selectedCompany, selectedInstitution, refreshTrigger]); 

    const executeEditTicket = (id: string, status: string, platformRemarks: string) => {
        let formattedStatus = status.toLowerCase();
        
        if (formattedStatus === 'in progress') {
            formattedStatus = 'in_progress';
        }
        
        editTickets(
            id,
            formattedStatus,
            platformRemarks,
            (res) => {
                toast.success("Ticket updated successfully!");
                setRefreshTrigger(prev => prev + 1); 
                setIsConfirmOpen(false); 
                setEditingTicket(null);
            },
            (err) => {
                toast.error(typeof err === 'string' ? err : "Failed to update ticket");
                setIsConfirmOpen(false);
            }
        );
    };

    const statusOptions = [
        { id: 'Open', label: 'Open' },
        { id: 'In Progress', label: 'In Progress' },
        { id: 'Closed', label: 'Closed' },
    ];

    const columns = [
        { field: 'slNo', headerName: 'Sl.No', width: 70, headerAlign: 'center', align: 'center' },
        { field: 'ticketNumber', headerName: 'Ticket Number', width: 150, headerAlign: 'center', align: 'center' },
        { field: 'issue', headerName: 'Issue', flex: 1,minWidth: 150, headerAlign: 'center', align: 'center' },
        { field: 'createdDate', headerName: 'Created Date', width: 130, headerAlign: 'center', align: 'center' },
        { field: 'remarks', headerName: 'Remarks', flex: 1, headerAlign: 'center', align: 'center' },
        {
            field: 'status',
            headerName: 'Status',
            width: 180,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const isClosed = params.value === 'Closed';
                
                return (
                    <Box 
                        display="flex" 
                        justifyContent="center" 
                        alignItems="center" 
                        width="100%" 
                        height="100%"
                        sx={{
                            pointerEvents: isClosed ? 'none' : 'auto', 
                            opacity: isClosed ? 0.6 : 1
                        }}
                    >
                        <Dropdown
                            options={statusOptions}
                            selectedValue={params.value as string}
                            handleChange={(event: SelectChangeEvent<string>) => {
                                setEditingTicket({
                                    id: String(params.row.id),
                                    status: event.target.value,
                                    remarks: params.row.response || '',
                                    ticketNumber: params.row.ticketNumber,
                                    editType: 'status'
                                });
                                setIsConfirmOpen(true);
                            }}
                            width='150px'
                            height='30px'
                            disabled={isClosed} 
                        />
                    </Box>
                );
            },
        },
        { field: 'assignedTo', headerName: 'Assigned To', width: 120, headerAlign: 'center', align: 'center' },
        { 
            field: 'response', 
            headerName: 'Response', 
            width: 200, 
            headerAlign: 'center', 
            align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <ResponseCell 
                        params={params}
                        onDirectSubmit={(id, currentStatus, updatedRemarks) => {
                            executeEditTicket(id, currentStatus, updatedRemarks);
                        }}
                        onBlurSubmit={(id, currentStatus, updatedRemarks) => {
                            setEditingTicket({ 
                                id, 
                                status: currentStatus, 
                                remarks: updatedRemarks,
                                ticketNumber: params.row.ticketNumber,
                                editType: 'response'
                            });
                            setIsConfirmOpen(true);
                        }} 
                    />
                );
            }
        },
    ];

    const handlePageClick = (event: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage - 1);
    };

    const updateField = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        if (!isNaN(Number(value))) {
            setPageSize(Number(value));
            setPage(0);
        }
    };

    const showEntries = [
        { id: '10', label: '10' },
        { id: '20', label: '20' },
        { id: '50', label: '50' },
        { id: '100', label: '100' },
    ];

    return (
        <>
            <Header />
            <div style={{ marginTop: "70px" }}>
                <Heading
                    title="Tickets"
                    closeIcon="false"
                    leftContent={
                        <>
                            <Box display="flex" alignItems="center" marginLeft='10px' gap="15px">
                                <Box display="flex" alignItems="center">
                                    <Typography style={{ marginRight: '5px', fontSize: '14px' }}>Show Entries:</Typography>
                                    <Dropdown options={showEntries} selectedValue={pageSize.toString()} handleChange={updateField} width='60px' height='30px' />
                                </Box>

                                <Box display="flex" alignItems="center">
                                    <Dropdown
                                        options={companyOptions}
                                        selectedValue={selectedCompany}
                                        placeholder="Select Company"
                                        handleChange={(event: SelectChangeEvent<string>) => {
                                            setSelectedCompany(event.target.value);
                                            setPage(0); 
                                        }}
                                        width='150px'
                                        height='30px'
                                        searchable={true}
                                    />
                                </Box>
                            </Box>
                        </>
                    }
                    rightContent={
                        <>
                            <Box display="flex" alignItems="center" gap="15px" marginRight={"10px"}>
                                {companyOptions.find(c => c.id === selectedCompany)?.label.toLowerCase() === 'stms' && (
                                    <Box display="flex" alignItems="center">
                                        <Dropdown 
                                            options={institutionOptions} 
                                            selectedValue={selectedInstitution} 
                                            placeholder="Select Institution" 
                                            handleChange={(e: SelectChangeEvent<string>) => {
                                                setSelectedInstitution(e.target.value);
                                                setPage(0);
                                            }} 
                                            width='150px' 
                                            height='30px' 
                                        />
                                    </Box>
                                )}
                                
                                <Button
                                    className="custom-button"
                                    style={{ backgroundColor: '#900000', color: 'white', fontWeight: 'bold' }}
                                    onClick={() => setIsDialogOpen(true)} 
                                >
                                    + Tickets
                                </Button>
                            </Box>
                        </>
                    }
                />
                
                <Box className="table-container custom-data-grid" sx={{ marginLeft: { xs: '10px', sm: '180px' }, marginRight: '40px', position: 'relative', height: '500px' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        sx={{ 
                            height: '100%', 
                            '& .MuiDataGrid-cell:focus': { outline: 'none' },
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5', fontWeight: 'bold' },
                            '& .MuiDataGrid-cell': { borderRight: '1px solid #e0e0e0' },
                            '& .MuiDataGrid-columnHeader': { borderRight: '1px solid #e0e0e0' }
                        }}
                        getRowHeight={() => 'auto'}
                        disableRowSelectionOnClick
                        hideFooterPagination
                    />
                </Box>
                
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                    <Newpagination rows={totalRecords} pageSize={pageSize} page={page} handlePageChange={handlePageClick} />
                </div>
            </div>
            <NavBar selected="Tickets" />

            <AddTicketDialog 
                open={isDialogOpen} 
                handleClose={() => setIsDialogOpen(false)} 
                initialCompanyId={selectedCompany} 
                companyOptions={companyOptions}
                onSuccess={() => {
                    setPage(0);
                    setRefreshTrigger(prev => prev + 1);
                    setIsDialogOpen(false);
                }}
            />

            <ConfirmationPopup 
                open={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                title="Confirm Update"
                message="Are you sure you want to update this ticket?"
                
                contentToDisplay={
                    editingTicket && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: '4px', border: '1px solid #ddd' }}>
                            {editingTicket.editType === 'status' ? (
                                <Typography variant="body2">
                                    <strong>Status:</strong> {editingTicket.status}
                                </Typography>
                            ) : (
                                <>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Ticket Number:</strong> {editingTicket.ticketNumber}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Response:</strong> {editingTicket.remarks || <em>No response provided</em>}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    )
                }
                
                onConfirm={() => {
                    if (editingTicket) {
                        executeEditTicket(editingTicket.id, editingTicket.status, editingTicket.remarks);
                    }
                }}
            />
        </>
    );
};

export default TicketsPage;